import { useMemo } from "react";
import { Sparkles, RefreshCw, Send, Mic, ShieldCheck } from "lucide-react";
import type { DiscoveryCandidate } from "@/lib/discovery/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SignedImage from "@/components/media/SignedImage";

interface Props {
  candidate: DiscoveryCandidate;
  onRefresh?: () => void;
  onOpen?: (candidate: DiscoveryCandidate) => void;
  onSkip?: (candidate: DiscoveryCandidate) => void;
  refreshing?: boolean;
}

const DailySignalCard = ({
  candidate: c,
  onRefresh,
  onOpen,
  onSkip,
  refreshing = false,
}: Props) => {
  const cover = c.photos?.[0];
  const verified = c.bgc_verified || c.community_verified;
  const featuredPrompt = useMemo(() => {
    const first = c.prompts?.find((p) => p.answer?.trim());
    return first ? `${first.text} — ${first.answer}` : null;
  }, [c.prompts]);

  return (
    <section
      aria-label="Today's daily signal"
      className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-5 shadow-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          <Sparkles className="h-4 w-4" /> Daily Signal
        </div>
        {onRefresh && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh daily signal"
            className="h-8 gap-1 text-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        )}
      </div>

      <div className="mt-4 flex items-center gap-4">
        <div className="h-20 w-20 flex-none overflow-hidden rounded-2xl bg-muted">
          {cover && (
            <SignedImage
              src={cover}
              alt={c.display_name ?? "Member"}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-semibold">
            {c.display_name ?? "Member"}
          </p>
          {c.pronouns && (
            <p className="text-sm text-muted-foreground">{c.pronouns}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {verified && (
              <Badge variant="secondary" className="gap-1 rounded-full text-[10px]">
                <ShieldCheck className="h-3 w-3" /> Verified
              </Badge>
            )}
            {c.voice_intro_url && (
              <Badge variant="secondary" className="gap-1 rounded-full text-[10px]">
                <Mic className="h-3 w-3" /> Voice intro
              </Badge>
            )}
            {typeof c.distance_km === "number" && (
              <Badge variant="outline" className="rounded-full text-[10px]">
                {Math.round(c.distance_km)} km away
              </Badge>
            )}
          </div>
        </div>
      </div>

      {featuredPrompt && (
        <p className="mt-4 line-clamp-2 rounded-2xl bg-background/60 px-3 py-2 text-xs text-foreground/80">
          {featuredPrompt}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        {onOpen && (
          <Button
            type="button"
            size="sm"
            onClick={() => onOpen(c)}
            className="flex-1 gap-1"
          >
            <Send className="h-4 w-4" />
            Send signal
          </Button>
        )}
        {onSkip && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onSkip(c)}
            className="text-xs text-muted-foreground"
          >
            Not today
          </Button>
        )}
      </div>
    </section>
  );
};

export default DailySignalCard;
